import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AchBankFields, {
  achBankReady,
  type AchAccountType,
} from '../../Components/AchBankFields';
import PayflowHostedCardFrame, {
  payflowDeclineMessage,
  type PayflowHostedMessage,
} from '../../Components/PayflowHostedCardFrame';
import {
  completePayflowSession,
  createPayflowSession,
  enrollLoanACHRecurring,
  getPayflowSession,
  type CreatePayflowSessionResponse,
} from '../../endpoints/loans';
import { getApiErrorMessage } from '../../utils/httpClientTs';

type ElfLoanAccountOnFileDialogProps = {
  open: boolean;
  onClose: () => void;
  borrowerUserId: string;
  borrowerName?: string;
  /** Default monthly recurring amount (e.g. next expected receivable) */
  defaultAmount?: number | null;
  onSuccess: () => void;
};

const nextBillingStart = (day: number): string => {
  const today = new Date();
  const y = today.getUTCFullYear();
  const m = today.getUTCMonth();
  const d = today.getUTCDate();
  let year = y;
  let month = m;
  if (d >= day) {
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const useDay = Math.min(day, last);
  const dt = new Date(Date.UTC(year, month, useDay));
  return dt.toISOString().slice(0, 10);
};

const ElfLoanAccountOnFileDialog: React.FC<ElfLoanAccountOnFileDialogProps> = ({
  open,
  onClose,
  borrowerUserId,
  borrowerName,
  defaultAmount,
  onSuccess,
}) => {
  const [method, setMethod] = useState<'card' | 'bank'>('card');
  const [amount, setAmount] = useState('');
  const [billingDay, setBillingDay] = useState('15');
  const [startDate, setStartDate] = useState(nextBillingStart(15));
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountType, setAccountType] = useState<AchAccountType>('checking');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [session, setSession] = useState<CreatePayflowSessionResponse | null>(
    null
  );
  const [completing, setCompleting] = useState(false);
  const [waitingConfirm, setWaitingConfirm] = useState(false);
  const finalizeInFlightRef = useRef(false);
  const waitStartedRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setPending(false);
    setCompleting(false);
    setWaitingConfirm(false);
    finalizeInFlightRef.current = false;
    waitStartedRef.current = 0;
    setSession(null);
    setMethod('card');
    setRoutingNumber('');
    setAccountNumber('');
    setAccountType('checking');
    const day = 15;
    setBillingDay(String(day));
    setStartDate(nextBillingStart(day));
    if (
      defaultAmount != null &&
      !Number.isNaN(defaultAmount) &&
      defaultAmount > 0
    ) {
      setAmount(defaultAmount.toFixed(2));
    } else {
      setAmount('');
    }
  }, [open, defaultAmount]);

  const parsedAmount = Number.parseFloat(amount);
  const parsedDay = Number.parseInt(billingDay, 10);
  const canStart =
    Number.isFinite(parsedAmount) &&
    parsedAmount > 0 &&
    Number.isFinite(parsedDay) &&
    parsedDay >= 1 &&
    parsedDay <= 28 &&
    !!startDate;

  const bankReady = achBankReady(routingNumber, accountNumber, accountType);

  const handleEnrollBank = async () => {
    setError(null);
    if (!canStart || !bankReady) {
      setError('Enter amount, billing day (1–28), and a valid bank account.');
      return;
    }
    setPending(true);
    try {
      await enrollLoanACHRecurring({
        user_id: borrowerUserId,
        amount: Math.round(parsedAmount * 100) / 100,
        billing_day: parsedDay,
        routing_number: routingNumber.trim(),
        account_number: accountNumber.trim(),
        account_type: accountType,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setPending(false);
    }
  };

  const handleStart = async () => {
    setError(null);
    if (!canStart) {
      setError('Enter amount, billing day (1–28), and start date.');
      return;
    }
    setPending(true);
    try {
      const resp = await createPayflowSession({
        user_id: borrowerUserId,
        purpose: 'account_on_file',
        amount: Math.round(parsedAmount * 100) / 100,
        billing_day: parsedDay,
        start_date: startDate,
      });
      waitStartedRef.current = Date.now();
      setWaitingConfirm(false);
      setSession(resp);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setPending(false);
    }
  };

  const finalize = useCallback(async () => {
    if (!session || finalizeInFlightRef.current) return;
    finalizeInFlightRef.current = true;
    setCompleting(true);
    setError(null);
    try {
      await completePayflowSession(session.session_id);
      onSuccess();
      onClose();
    } catch (err) {
      setError(
        getApiErrorMessage(err) ||
          'Waiting for PayPal to confirm this card. Do not enter it again.'
      );
    } finally {
      setCompleting(false);
      finalizeInFlightRef.current = false;
    }
  }, [session, onSuccess, onClose]);

  useEffect(() => {
    if (!session) return;
    let stopped = false;
    const tick = async () => {
      if (stopped || finalizeInFlightRef.current) return;
      if (
        waitStartedRef.current &&
        Date.now() - waitStartedRef.current > 45000
      ) {
        setWaitingConfirm(true);
      }
      try {
        const st = await getPayflowSession(session.session_id);
        if (stopped) return;
        if (st.status === 'completed' || st.profile_id) {
          onSuccess();
          onClose();
          return;
        }
        if (
          st.status === 'declined' ||
          st.status === 'failed' ||
          st.status === 'expired'
        ) {
          setError(payflowDeclineMessage(st.respmsg, st.result));
          setSession(null);
          return;
        }
        if (st.status === 'approved' || st.has_pnref) {
          void finalize();
        }
      } catch {
        // Keep polling.
      }
    };
    const id = window.setInterval(() => void tick(), 2000);
    void tick();
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [session, finalize, onSuccess, onClose]);

  const onHostedMessage = (msg: PayflowHostedMessage) => {
    if (msg.status === 'approved') {
      void finalize();
      return;
    }
    if (msg.status === 'canceled') {
      setError('Card entry was canceled.');
      setSession(null);
      return;
    }
    setError(payflowDeclineMessage(msg.respmsg, msg.result));
    setSession(null);
  };

  return (
    <Dialog
      open={open}
      onClose={() => !pending && !completing && onClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Add account on file</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {borrowerName
            ? `Enroll monthly autopay for ${borrowerName}.`
            : 'Enroll monthly autopay.'}{' '}
          {method === 'card'
            ? 'A $1 authorization validates the card and is voided after the profile is created. No installment is charged now.'
            : 'The bank account is saved with Payflow for monthly drafts. No payment is taken now. Enrolling replaces any other active autopay profile.'}
        </Typography>

        {!session && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={method}
            onChange={(_, value) => value && setMethod(value)}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="card">Card</ToggleButton>
            <ToggleButton value="bank">Bank account</ToggleButton>
          </ToggleButtonGroup>
        )}

        {!session ? (
          <Stack spacing={2}>
            <TextField
              label="Recurring amount"
              type="number"
              size="small"
              fullWidth
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ min: 0.01, step: 0.01 }}
            />
            <TextField
              label="Billing day of month"
              type="number"
              size="small"
              fullWidth
              value={billingDay}
              onChange={(e) => {
                setBillingDay(e.target.value);
                const d = Number.parseInt(e.target.value, 10);
                if (d >= 1 && d <= 28) {
                  setStartDate(nextBillingStart(d));
                }
              }}
              inputProps={{ min: 1, max: 28 }}
              helperText="Day 1–28 each month"
            />
            {method === 'card' && (
              <TextField
                label="First billing date"
                type="date"
                size="small"
                fullWidth
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            )}
            {method === 'bank' && (
              <AchBankFields
                routingNumber={routingNumber}
                accountNumber={accountNumber}
                accountType={accountType}
                onRoutingNumber={setRoutingNumber}
                onAccountNumber={setAccountNumber}
                onAccountType={setAccountType}
                disabled={pending}
              />
            )}
          </Stack>
        ) : (
          <PayflowHostedCardFrame
            iframeUrl={session.iframe_url}
            sessionId={session.session_id}
            onMessage={onHostedMessage}
          />
        )}
        {completing && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Creating recurring profile…
          </Alert>
        )}
        {session && !completing && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            If PayPal showed a receipt, do not enter the card again.
          </Alert>
        )}
        {waitingConfirm && !completing && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Still waiting for PayPal confirmation. Do not start a second card
            enrollment.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending || completing}>
          Cancel
        </Button>
        {!session ? (
          method === 'bank' ? (
            <Button
              variant="contained"
              disabled={!canStart || !bankReady || pending}
              onClick={() => void handleEnrollBank()}
            >
              {pending ? 'Enrolling…' : 'Enroll bank account'}
            </Button>
          ) : (
            <Button
              variant="contained"
              disabled={!canStart || pending}
              onClick={() => void handleStart()}
            >
              {pending ? 'Preparing…' : 'Continue to card entry'}
            </Button>
          )
        ) : (
          <Button
            variant="outlined"
            disabled={completing}
            onClick={() => {
              setError(
                'Go back only if you have not submitted the card. Starting over can charge or enroll twice.'
              );
              setSession(null);
            }}
          >
            Back
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ElfLoanAccountOnFileDialog;
